// --------------------------------------------------------------
// Created On: 2025-10-31
// Author: Hannah Vaughan
//
// Last Modified: 2025-12-05
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import styles from "./SuperAdminDeactivateDevicesPage.module.scss";
import useApi from "src/hooks/useApi";
import deepCopy from "src/utilities/deepCopy";
import { API } from "src/constants/miscellaneous";
import Spinner from "src/components/Spinner/Spinner";
import DeviceEntry from "./DeviceEntry/DeviceEntry";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import ImportCsvModal from "./ImportCsvModal/ImportCsvModal";
import getApiError from "src/utilities/api/getApiError";
import apiRequest from "src/utilities/api/apiRequest";
import Toast from "src/components/Toast/Toast";
import useReduxSelector from "src/hooks/useReduxSelector";
import DeactivateDevicesHeader from "./DeactivateDevicesHeader/DeactivateDevicesHeader";

// Batch updates the activation statuses of multiple devices.
export default function SuperAdminDeactivateDevicesPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSubmitModal, setShowSubmitModal] = useState<boolean>(false);
  const [showImportCsvModal, setShowImportCsvModal] = useState<boolean>(false);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [devices, setDevices] = useState<DeviceCard[]>([]);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Gets all device types from the API.
  useApi(
    () => {
      setLoading(true);
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/device`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setDeviceTypes(responseBody.deviceTypes);
        setErrorMessage("");
      } else {
        setErrorMessage(await getApiError(response, "Unable to get device types."));
      }
      setLoading(false);
    },
    []
  );

  // Adds all devices read in from a CSV file to the list of devices on the page.
  function handleUploadedDevicesChange(devicesList: Device[]): void {
    const devicesDeepCopy = deepCopy(devices);
    let formId = 1;
    devicesDeepCopy.forEach((existingDevice) => {
      if (existingDevice.formId >= formId) {
        formId = existingDevice.formId + 1;
      }
    });
    devicesList.forEach((device) => {
      const newDevice: DeviceCard = {
        formId: formId,
        deviceTypeId: device.deviceTypeId,
        identifier: device.identifier,
        activationStatus: device.activationStatus,
      };
      devicesDeepCopy.push(newDevice);
      formId++;
    });
    setDevices(devicesDeepCopy);
    setShowImportCsvModal(false);
  }

  // Update the activation statuses of all listed devices if each one can be validated.
  async function batchDeactivateDevices(devicesList: Device[]): Promise<void> {
    let errorMessage = "";
    for (const device of devicesList) {
      const [message, isValidDevice] = await deviceIsValid(
        device.deviceTypeId,
        device.identifier,
        device.activationStatus
      );
      // Set the error message and break if any device is found to be invalid.
      if (!isValidDevice) {
        errorMessage = message;
        break;
      }
    }

    // Batch update the devices to the requested activation statuses since all of them have been validated.
    if (errorMessage === "") {
      setLoading(true);
      const requestBody = {
        devices: devicesList,
      };
      const [response, responseBody] = (await apiRequest(
        `${API}/modify-device-activation-status`,
        "POST",
        requestBody
      )) as [Response, MessageResponseBody];
      if (response.ok && responseBody) {
        setSuccessMessage(responseBody.message);
        setSubmitErrorMessage("");
        setShowSubmitModal(false);
      } else {
        setSuccessMessage("");
        setSubmitErrorMessage(await getApiError(response, "Unable to batch update device activation statuses."));
      }
      setLoading(false);
    } else {
      setSubmitErrorMessage(errorMessage);
    }
  }

  // Checks if the current device is valid for updates.
  async function deviceIsValid(
    deviceTypeId: number,
    identifier: string,
    activationStatus: string
  ): Promise<[errorMessage: string, validDevice: boolean]> {
    // Get the name of the selected device type based on the provided ID.
    const deviceType = deviceTypes.find((type) => type.deviceTypeId === deviceTypeId);
    let deviceTypeModel = "";
    let deviceTypeName = "";
    if (deviceType !== undefined && deviceType !== null) {
      deviceTypeModel = deviceType.model;
      deviceTypeName = deviceType.name;
    }

    if (deviceTypeModel === "") {
      // Device type model will be empty if none could be found for the provided ID.
      return ["A valid device type must be selected for all devices.", false];
    } else if (!(await deviceIdentifierIsValid(deviceTypeModel, identifier))) {
      // Device identifier is not valid if the combination of the type and identifier do not match an existing device.
      return [`${deviceTypeName} device with identifier "${identifier}" could not be found.`, false];
    } else if (activationStatus !== "Deactivated" && activationStatus !== "Ready for activation") {
      return ['Activation Status must either be "Deactivated" or "Ready for activation" for all devices.', false];
    } else {
      return ["", true];
    }
  }

  // Finds an existing device given a device type name and a device identifier
  async function deviceIdentifierIsValid(type: string, identifier: string): Promise<boolean> {
    setLoading(true);
    const requestBody = {
      deviceType: type,
      deviceIdentifier: identifier,
    };
    const [response, responseBody] = (await apiRequest(`${API}/device/search`, "POST", requestBody)) as [
      Response,
      DeviceSearchPostResponseBody
    ];
    setLoading(false);
    if (response.ok && responseBody && responseBody.devices.length > 0) {
      // If at least one device could be found with the provided type and identifier,
      // then the device identifier is valid.
      return true;
    } else {
      // If no devices could be found with the provided type and identifier,
      // then the exact device could not be found and validated.
      return false;
    }
  }

  // Creates a device card for the user to fill out.
  function handleCreateDevice(): void {
    const devicesDeepCopy = deepCopy(devices);
    let formId = 1;
    devicesDeepCopy.forEach((device) => {
      if (device.formId >= formId) {
        formId = device.formId + 1;
      }
    });

    const device: DeviceCard = {
      formId: formId,
      deviceTypeId: -1,
      identifier: "",
      activationStatus: "",
    };
    devicesDeepCopy.push(device);
    setDevices(devicesDeepCopy);
  }

  // Updates the device card depending on if the device type, identifier, or activation status are updated.
  function handleUpdateDevice(
    formId: number,
    deviceTypeId: number,
    identifier: string,
    activationStatus: string
  ): void {
    const devicesDeepCopy = deepCopy(devices);
    const device = devicesDeepCopy.find((device) => device.formId === formId);
    if (device !== undefined) {
      device.deviceTypeId = deviceTypeId;
      device.identifier = identifier;
      device.activationStatus = activationStatus;
    }
    setDevices(devicesDeepCopy);
  }

  // Deletes the device card from the page.
  function handleDeleteDevice(formId: number): void {
    const devicesDeepCopy = deepCopy(devices);
    const selectedDeviceIndex = devicesDeepCopy.findIndex((device) => device.formId === formId);
    if (selectedDeviceIndex >= 0) {
      devicesDeepCopy.splice(selectedDeviceIndex, 1);
      setDevices(devicesDeepCopy);
    }
  }

  return (
    <div className="container p-4">
      <Spinner loading={loading} />
      <div className={`${styles.body} shadow-sm`}>
        <DeactivateDevicesHeader
          disabled={loading}
          deviceCount={devices.length}
          onSubmit={() => setShowSubmitModal(true)}
          onImportCsv={() => setShowImportCsvModal(true)}
          onClearAll={() => setDevices([])}
        />
        <div className="m-4">
          <div className="row align-items-center mx-1">
            <div className="col g-0">
              {devices !== null &&
                devices !== undefined &&
                devices.map((device) => (
                  <DeviceEntry
                    key={device.formId}
                    formId={device.formId}
                    deviceTypeId={device.deviceTypeId}
                    identifier={device.identifier}
                    activationStatus={device.activationStatus}
                    deviceTypes={deviceTypes}
                    disabled={loading}
                    onCreate={() => handleCreateDevice()}
                    onChange={(formId, deviceTypeId, identifier, activationStatus) =>
                      handleUpdateDevice(formId, deviceTypeId, identifier, activationStatus)
                    }
                    onDelete={(formId) => handleDeleteDevice(formId)}
                  />
                ))}
            </div>
          </div>
          <div className="d-flex justify-content-center mt-4 mx-1">
            <button type="button" className="btn btn-primary w-25" onClick={() => handleCreateDevice()}>
              Add Device
            </button>
          </div>
        </div>
      </div>

      {/* Error Message Toast */}
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => setErrorMessage("")}
        type="error"
      />

      {/* Success Message Toast */}
      <Toast
        title="Device Activation Status(es) Updated"
        message={successMessage}
        show={successMessage.length > 0}
        onClose={() => {
          setSuccessMessage("");
          setErrorMessage("");
        }}
        type="success"
      />

      <ConfirmModal
        showModal={showSubmitModal}
        title="Modify activation status?"
        content="Are you sure you want to modify the activation statuses for the selected devices?"
        yesText="Modify Activation Statuses"
        noText="Cancel"
        errorMessage={submitErrorMessage}
        onClose={() => {
          setSubmitErrorMessage("");
          setShowSubmitModal(false);
        }}
        onYes={() => batchDeactivateDevices(devices)}
        onNo={() => {
          setSubmitErrorMessage("");
          setShowSubmitModal(false);
        }}
      />

      <ImportCsvModal
        showModal={showImportCsvModal}
        errorMessage={errorMessage}
        defaultDeviceTypeId={-1}
        deviceTypes={deviceTypes}
        onClose={() => setShowImportCsvModal(false)}
        onUploadedDevicesListChange={(devicesList) => handleUploadedDevicesChange(devicesList)}
        onErrorMessageChange={(message) => setErrorMessage(message)}
      />
    </div>
  );
}

interface ResponseBody {
  deviceTypes: DeviceType[];
}

interface DeviceSearchPostResponseBody {
  devices: ProcessedDevice[];
}

interface MessageResponseBody {
  message: string;
}

interface DeviceType {
  deviceTypeId: number;
  model: string;
  name: string;
}

interface Device {
  deviceTypeId: number;
  identifier: string;
  activationStatus: string;
}

interface ProcessedDevice {
  deviceType: string;
  deviceIdentifier: string;
  companyId: number;
}

interface DeviceCard {
  formId: number;
  deviceTypeId: number;
  identifier: string;
  activationStatus: string;
}
